/**
 * App.tsx — top-level router + provider tree.
 *
 * Replaces:
 *   - ui-router $stateProvider config in app.js
 *   - $stateChangeStart auth guard in app.js run()
 *   - AppCtrl (notAuthenticated → logout + redirect to login)
 *
 * Provider order:
 *   MantineProvider
 *     ModalsProvider          (for modals.openConfirmModal used in DashboardPage)
 *       Notifications         (for notifications.show used in SmsModal etc.)
 *         QueryClientProvider (TanStack Query — available for future server-state hooks)
 *           AuthProvider
 *             SocketProvider  (needs driver + isAuthenticated from AuthProvider)
 *               Router
 *
 * AppShell wraps its pages in RunsheetProvider, below its login guard, so the runsheet,
 * the selected stop, the stations and the driver's position mount at login and are
 * dropped at logout. It is not part of the provider order above.
 *
 * Every route renders inside SafeAreaShell, the one element that sets the app's height
 * and clears the notch and home indicator. Pages fill it with h="100%".
 *
 * The theme sizes every control for the Touch floor (44px) or above, because drivers
 * tap with gloves on. A call site only sets a size to go up from these defaults.
 */

import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  createTheme,
  MantineProvider,
  NumberInput,
  PasswordInput,
  Select,
  Textarea,
  TextInput,
} from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import '@mantine/core/styles.css';
import '@mantine/notifications/styles.css';

import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { RunsheetProvider } from '@/contexts/RunsheetContext';
import { SocketProvider } from '@/contexts/SocketContext';
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import ChatPage from '@/pages/ChatPage';
import RunMapPage from '@/pages/RunMapPage';

const queryClient = new QueryClient({
  defaultOptions: { queries: { retry: 1, staleTime: 30_000 } },
});

// Mantine `md` is 42px, under the Touch floor. `lg` is 50px and ActionIcon `xl` is 44px.
const theme = createTheme({
  components: {
    Button: Button.extend({ defaultProps: { size: 'lg' } }),
    Select: Select.extend({ defaultProps: { size: 'lg' } }),
    NumberInput: NumberInput.extend({ defaultProps: { size: 'lg' } }),
    TextInput: TextInput.extend({ defaultProps: { size: 'lg' } }),
    PasswordInput: PasswordInput.extend({ defaultProps: { size: 'lg' } }),
    Textarea: Textarea.extend({ defaultProps: { size: 'lg' } }),
    ActionIcon: ActionIcon.extend({ defaultProps: { size: 'xl' } }),
    Checkbox: Checkbox.extend({ defaultProps: { size: 'lg' } }),
  },
});

// ── Safe-area shell — the app's full height, clear of the notch and home indicator ──

function SafeAreaShell({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();

  return (
    <Box
      // The side and bottom insets show the page's own background.
      bg={pathname === '/login' ? 'gray.1' : undefined}
      style={{
        height: '100dvh',
        // The top inset is a border so it can be green while the other insets aren't.
        // #4caf50 matches the theme-color meta, which keeps the white iOS status bar
        // text readable.
        borderTop: 'env(safe-area-inset-top) solid #4caf50',
        paddingRight: 'env(safe-area-inset-right)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        paddingLeft: 'env(safe-area-inset-left)',
      }}
    >
      {children}
    </Box>
  );
}

// ── Persistent app shell — keeps Dashboard, Chat and the Run map mounted ─────

function AppShell() {
  const { isAuthenticated, initialising } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // The Run map mounts on its first visit, so a driver who never opens it never loads
  // Google's script. It then stays mounted, so a session costs one billed map load.
  const [mapOpened, setMapOpened] = useState(false);
  if (pathname === '/map' && !mapOpened) setMapOpened(true);

  useEffect(() => {
    if (!initialising && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, initialising, navigate]);

  if (initialising || !isAuthenticated) return null;

  return (
    <RunsheetProvider>
      <div style={{ display: pathname === '/dashboard' ? 'contents' : 'none' }}>
        <DashboardPage />
      </div>
      <div style={{ display: pathname === '/chat' ? 'contents' : 'none' }}>
        <ChatPage />
      </div>
      {mapOpened && (
        <div style={{ display: pathname === '/map' ? 'contents' : 'none' }}>
          <RunMapPage />
        </div>
      )}
    </RunsheetProvider>
  );
}

// ── Socket wrapper (needs auth context) ─────────────────────────────────────

function SocketLayer({ children }: { children: React.ReactNode }) {
  const { driver, isAuthenticated } = useAuth();
  return (
    <SocketProvider driver={driver} isAuthenticated={isAuthenticated}>
      {children}
    </SocketProvider>
  );
}

// ── App ──────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <MantineProvider theme={theme}>
      {/* Confirm dialogs and alerts stay small centred cards in every size class. Their
          close button goes up to the Touch floor. */}
      <ModalsProvider
        modalProps={{ centered: true, closeButtonProps: { size: 'xl', 'aria-label': 'Close' } }}
      >
        {/* Margins, not top/right, because the style lands on the container of every
            position and an offset would stretch the bottom ones over the page. */}
        <Notifications
          position="top-right"
          style={{
            marginTop: 'env(safe-area-inset-top)',
            marginRight: 'env(safe-area-inset-right)',
          }}
        />
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <SocketLayer>
                <SafeAreaShell>
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/dashboard" element={<AppShell />} />
                    <Route path="/chat" element={<AppShell />} />
                    <Route path="/map" element={<AppShell />} />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </SafeAreaShell>
              </SocketLayer>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </ModalsProvider>
    </MantineProvider>
  );
}
